import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { map } from 'rxjs/operators';

import { BaseService } from 'src/app/core/services/base.service';
import { CacheResolverService } from '../../core/cache/cache-resolver.service';

import { CacheConstant } from 'src/app/core/cache/cache.constant';
import moment from 'moment';
import { Duration } from 'moment';
import { BehaviorSubject } from 'rxjs';
import { SessionStorageService } from 'ngx-webstorage';
import jwt_decode from 'jwt-decode';
import { OktaAuth } from 'src/app/shared/models/app';
import { OktaConstant } from '../constants/okta.constant';
import { Environment } from '../environment/environment';

@Injectable({
  providedIn: 'root',
})
export class TokenService extends BaseService {
  /*********************Properties*********************/
  apigee = {
    token: null,
    sessionValidationTime: 2 * 60 * 1000,
    sessionInterval: null,
    tokenExpired$: new BehaviorSubject<boolean>(false),
    tokenExpiring$: new BehaviorSubject<any>({
      expiring: false,
      time: null,
    }),
  } as any;
  
  /*********************Properties*********************/
  /*********************Constructor*********************/
  constructor(http: HttpClient, private readonly cache: CacheResolverService, private sessionStorage: SessionStorageService) {
    super(http);
  }
  /*********************Constructor*********************/

  /*********************Utility Methods*********************/

  init() {
    this.verifyApigeeTokenExpiration();
  }

  validate(accessToken: string, idToken: string) {
    const options = {
      headers: {
        Authorization: `Bearer ${idToken}, Bearer ${accessToken}`,
      }
    };

    return super
      .post(
        Environment.getInstance().appConfig.apigeeTokenEndpoint,
        null,
        options
      )
      .pipe(
        map((response: any) => {
          if (!response.hasError) {
            this.set(response.data.access_token);
          }

          return response;
        })
      );
  }

  set(
    token: string
  ): void {
    this.sessionStorage.store(CacheConstant.SessionStorageData.AuthToken, token);
  }

  getAccessToken() {
    if (this.sessionStorage.retrieve(CacheConstant.SessionStorageData.AccessToken) &&
      this.sessionStorage.retrieve(CacheConstant.SessionStorageData.IdToken)) {
      return Promise.resolve(true);
    }
    return Promise.resolve(false);
  }

  renew() {

    let refreshToken = this.sessionStorage.retrieve(CacheConstant.SessionStorageData.RefreshToken);
    let url = Environment.getInstance().oktaConfig.oktaTokenEndpoint;
    const body = new URLSearchParams();
    body.set("client_id", Environment.getInstance().oktaConfig.clientId);
    body.set("redirect_uri", window.location.origin + '/auth-code/callback');
    body.set("grant_type", 'refresh_token');
    body.set("scope", 'openid profile email offline_access');
    body.set("refresh_token", refreshToken);
    return this.http.post(url, body.toString(),
      {
        headers: {
          'Content-Type':
            'application/x-www-form-urlencoded',
          'Authorization': ''
        }
      });

  }

  /*********************Private Methods*********************/

  private verifyApigeeTokenExpiration() {
    let accessToken = this.sessionStorage.retrieve(CacheConstant.SessionStorageData.AccessToken)
    if(accessToken){
      let decodedAccessToken: any = jwt_decode(accessToken);
      const expiryTime = this.getExpiryTime(decodedAccessToken);
      if (expiryTime.asSeconds() <= 0) {
        this.clearSessionInterval(this.apigee.sessionInterval);
        this.apigee.tokenExpired$.next(true);
        return;
      }

      this.clearSessionInterval(this.apigee.sessionInterval);

      if (expiryTime.asMilliseconds() <= this.apigee.sessionValidationTime) {
        const tokenExpiration: any = {
          expiring: true,
          time: expiryTime,
        };
        let counter = this.cache.get(CacheConstant.LocalStorage.RefreshTokenCounter) ? this.cache.get(CacheConstant.LocalStorage.RefreshTokenCounter) : 1;
        if (counter < OktaConstant.LoggedInHours) {
          this.apigee.tokenExpiring$.next(tokenExpiration);
          this.cache.set(CacheConstant.LocalStorage.RefreshTokenCounter, counter + 1);
        }
        this.startApigeeSessionInterval(20000);
      } else {
        const timeout = expiryTime.asMilliseconds() - this.apigee.sessionValidationTime;
        this.startApigeeSessionInterval(timeout);
      }
    }
  }


  startApigeeSessionInterval(timeOut: number) {
    this.apigee.sessionInterval = setInterval(
      () => this.verifyApigeeTokenExpiration(),
      timeOut
    );
  }


  clearSessionInterval(interval: any) {
    clearInterval(interval);
  }

  getExpiryTime(token: any): Duration {
    let expiryTime: Duration = moment.duration();
    if (token) {
      const tokenExpiry = moment(token.exp * 1000);
      const today = moment();

      expiryTime = moment.duration(tokenExpiry.diff(today));
    }

    return expiryTime;
  }
}
